import { type ChannelTag, getTagData } from './channels-tag';
import { type CommunityBase, createCommunityBase } from './community';
import { type GivingBase, createGivingBase } from './giving';
import { type ProgramSlim, createProgramSlim } from './program';
import { type ProgramEventSlim, getProgramEventSlimData } from './program-event';
import type {
  ChannelBaseDataObject,
  ChannelCommunityEntityDataObject,
  ChannelDetailDataObject,
  ChannelEntityDataObject,
  ChannelEventEntityDataObject,
  ChannelGivingEntityDataObject,
  ChannelProgramEntityDataObject,
  ChannelVideoEntityDataObject,
} from './service';
import { type VideoListItem, createVideoListItem } from './video';

export type ChannelBase = {
  active: boolean;
  channelTags: ChannelTag[];
  description: string;
  id: string;
  name: string;
  priority: number;
};

export type ChannelDetail = {
  allCount: number;
  communityCount: number;
  eventCount: number;
  givingCount: number;
  programCount: number;
  videoCount: number;
} & ChannelBase;

export const getChannelDetailData = (data: ChannelDetailDataObject): ChannelDetail => ({
  ...getChannelBaseData(data),
  allCount: data.all_count,
  communityCount: data.community_count,
  eventCount: data.event_count,
  givingCount: data.giving_count,
  programCount: data.program_count,
  videoCount: data.video_count,
});

export type ChannelCommunityEntity = {
  community: CommunityBase;
  type: 'Community';
};

export type ChannelEntity =
  | ChannelCommunityEntity
  | ChannelEventEntity
  | ChannelGivingEntity
  | ChannelProgramEntity
  | ChannelVideoEntity;

export type ChannelEventEntity = {
  event: ProgramEventSlim;
  type: 'Event';
};

export type ChannelGivingEntity = {
  giving: GivingBase;
  type: 'Giving';
};

export type ChannelProgramEntity = {
  program: ProgramSlim;
  type: 'Program';
};

export type ChannelVideoEntity = {
  type: 'Video';
  video: VideoListItem;
};

export const getChannelBaseData = (data: ChannelBaseDataObject): ChannelBase => ({
  active: data.active,
  channelTags: data.channel_tags.map((tag) => getTagData(tag)),
  description: data.description,
  id: data.id,
  name: data.name,
  priority: data.priority,
});

const getChannelProgramEntity = (data: ChannelProgramEntityDataObject): ChannelProgramEntity => ({
  program: createProgramSlim(data.program),
  type: data.type,
});

const getChannelCommunityEntity = (data: ChannelCommunityEntityDataObject): ChannelCommunityEntity => ({
  community: createCommunityBase(data.community),
  type: data.type,
});

const getChannelEventEntity = (data: ChannelEventEntityDataObject): ChannelEventEntity => ({
  event: getProgramEventSlimData(data.event),
  type: data.type,
});

const getChannelGivingEntity = (data: ChannelGivingEntityDataObject): ChannelGivingEntity => ({
  giving: createGivingBase(data.giving),
  type: data.type,
});

const getChannelVideoEntity = (data: ChannelVideoEntityDataObject): ChannelVideoEntity => ({
  type: data.type,
  video: createVideoListItem(data.video),
});

export const getChannelEntityData = (data: ChannelEntityDataObject): ChannelEntity => {
  switch (data.type) {
    case 'Community':
      return getChannelCommunityEntity(data);
    case 'Event':
      return getChannelEventEntity(data);
    case 'Giving':
      return getChannelGivingEntity(data);
    case 'Program':
      return getChannelProgramEntity(data);
    case 'Video':
      return getChannelVideoEntity(data);
  }
};

export const getNestedEntity = (entity: ChannelEntity) => {
  switch (entity.type) {
    case 'Community':
      return { data: entity.community, type: entity.type };
    case 'Event':
      return { data: entity.event, type: entity.type };
    case 'Giving':
      return { data: entity.giving, type: entity.type };
    case 'Program':
      return { data: entity.program, type: entity.type };
    case 'Video':
      return { data: entity.video, type: entity.type };
  }
};

export type EntityOptions = {
  entityType: 'community' | 'event' | 'giving' | 'group' | 'organization' | 'program' | 'video';
};
